<template>
  <div class="res-widget" :class="{ 'is-busy': !!pending }">
    <div class="res-widget__top">
      <select :value="nodeId" class="form-control form-control-sm" :disabled="!!pending" @change="onNodeChange">
        <option value="">-- Node --</option>
        <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
      </select>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        title="Refresh"
        :disabled="!nodeId || !!pending"
        @click="refresh"
      >
        <i class="fas" :class="pending === 'refresh' ? 'fa-spinner fa-spin' : 'fa-sync-alt'" />
      </button>
    </div>

    <div class="res-widget__transport">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        :disabled="!nodeId || !!pending"
        title="Previous column"
        @click="run('prev', () => cmd('prevColumn'))"
      >
        <i class="fas fa-step-backward" />
      </button>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        :disabled="!nodeId || !!pending"
        title="Next column"
        @click="run('next', () => cmd('nextColumn'))"
      >
        <i class="fas fa-step-forward" />
      </button>
      <button
        type="button"
        class="btn btn-sm btn-warning"
        :disabled="!nodeId || !!pending"
        @click="run('clear', () => cmd('clearAll'))"
      >
        Clear
      </button>
    </div>

    <div class="res-widget__section">
      <div class="res-widget__section-title">
        <span>Columns</span>
        <span v-if="compositionName" class="res-widget__hint">{{ compositionName }}</span>
      </div>
      <div v-if="!nodeId" class="res-widget__empty">Select a node</div>
      <div v-else-if="!columns.length && pending !== 'refresh'" class="res-widget__empty">
        No columns — enable Resolume Webserver (port 8080)
      </div>
      <div v-else class="res-col-grid">
        <button
          v-for="c in columns"
          :key="c.index"
          type="button"
          class="res-col"
          :class="{ 'is-pending': pending === `col:${c.index}` }"
          :disabled="!!pending"
          :title="c.name"
          @click="triggerColumn(c.index)"
        >
          <span class="res-col__num">{{ c.index }}</span>
          <span class="res-col__name">{{ displayName(c) }}</span>
        </button>
      </div>
    </div>

    <div v-if="layers.length" class="res-widget__section">
      <div class="res-widget__section-title"><span>Clear layer</span></div>
      <div class="res-chip-row">
        <button
          v-for="l in layers"
          :key="l.index"
          type="button"
          class="res-chip"
          :class="{ 'is-pending': pending === `layer:${l.index}` }"
          :disabled="!!pending"
          @click="clearLayer(l.index)"
        >
          {{ displayName(l) }}
        </button>
      </div>
    </div>

    <div v-if="error" class="res-widget__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
  },
  data() {
    return {
      compositionName: '',
      columns: [],
      layers: [],
      pending: '',
      error: '',
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    nodeId() {
      return this.config.nodeId || ''
    },
  },
  watch: {
    nodeId: {
      immediate: true,
      handler(id) {
        this.compositionName = ''
        this.columns = []
        this.layers = []
        this.error = ''
        this.pending = ''
        if (id) this.refresh()
      },
    },
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    ...mapActions('workspace', ['updateWidgetConfig']),
    onNodeChange(e) {
      if (!this.widgetId) return
      this.updateWidgetConfig({ widgetId: this.widgetId, config: { nodeId: e.target.value } })
    },
    displayName(item) {
      if (!item) return ''
      const n = (item.name || '').trim()
      if (!n || /^column \d+$/i.test(n) || /^layer \d+$/i.test(n)) return `#${item.index}`
      return n
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) throw new Error('No response from node')
      if (msg.type === 'error' || msg.error) {
        throw new Error(msg.error || 'Command failed')
      }
      let payload = msg.payload
      if (payload == null) return null
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          throw new Error('Invalid response payload')
        }
      }
      return payload
    },
    normalizeItems(items, prefix) {
      if (!Array.isArray(items)) return []
      return items.map((item, i) => {
        if (!item || typeof item !== 'object') return null
        let index = Number(item.index)
        if (!Number.isFinite(index) || index < 1) index = i + 1
        let name = item.name
        if (name && typeof name === 'object' && name.value != null) name = name.value
        name = String(name || '').trim()
        if (!name) name = `${prefix} ${index}`
        return {
          index,
          id: item.id != null ? String(item.id) : '',
          name,
        }
      }).filter(Boolean)
    },
    async cmd(action, payload = {}) {
      if (!this.nodeId) return null
      const res = await this.sendCommand({
        node_id: this.nodeId,
        adapter: 'resolume',
        action,
        payload,
      })
      return this.parsePayload(res)
    },
    async run(key, fn) {
      if (this.pending) return
      this.pending = key
      this.error = ''
      try {
        await fn()
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Command failed'
      } finally {
        this.pending = ''
      }
    },
    async refresh() {
      if (!this.nodeId || this.pending) return
      this.pending = 'refresh'
      this.error = ''
      try {
        const data = await this.cmd('listComposition')
        if (!data) throw new Error('Empty composition response')
        this.compositionName = data.name || ''
        this.columns = this.normalizeItems(data.columns, 'column')
        this.layers = this.normalizeItems(data.layers, 'layer')
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Refresh failed'
        this.columns = []
        this.layers = []
      } finally {
        this.pending = ''
      }
    },
    triggerColumn(index) {
      const col = Number(index)
      if (!Number.isFinite(col) || col < 1) return
      this.run(`col:${col}`, () => this.cmd('triggerColumn', { columnIndex: col }))
    },
    clearLayer(index) {
      const layer = Number(index)
      if (!Number.isFinite(layer) || layer < 1) return
      this.run(`layer:${layer}`, () => this.cmd('clearLayer', { layerIndex: layer }))
    },
  },
  beforeDestroy() {
    this.pending = ''
  },
}
</script>

<style scoped>
.res-widget {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  height: 100%;
  min-height: 0;
  color: var(--ws-text);
  overflow: auto;
}
.res-widget__top {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}
.res-widget__top select {
  flex: 1;
  min-width: 0;
}
.res-widget__transport {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.res-widget__section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ws-text-muted, #6c757d);
  margin-bottom: 0.35rem;
}
.res-widget__hint {
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 55%;
}
.res-widget__empty {
  font-size: 0.78rem;
  color: var(--ws-text-muted, #6c757d);
  padding: 0.35rem 0;
}
.res-col-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 0.3rem;
}
.res-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  border: 1px solid var(--ws-widget-border, #ced4da);
  background: var(--ws-widget-bg, #fff);
  color: var(--ws-text);
  border-radius: 6px;
  padding: 0.4rem 0.45rem;
  text-align: left;
  cursor: pointer;
  min-height: 48px;
}
.res-col:hover:not(:disabled) {
  border-color: var(--ws-accent, #128c7e);
}
.res-col.is-pending {
  opacity: 0.65;
}
.res-col:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.res-col__num {
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--ws-accent, #128c7e);
}
.res-col__name {
  font-size: 0.75rem;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
.res-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.res-chip {
  border: 1px dashed var(--ws-widget-border, #ced4da);
  background: transparent;
  color: var(--ws-text);
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
}
.res-chip:hover:not(:disabled) {
  border-color: #dc3545;
  color: #dc3545;
}
.res-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.res-widget__error {
  font-size: 0.75rem;
  color: #dc3545;
}
</style>
